import React, { useState, useCallback, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import { observer } from "mobx-react-lite";
import CircularProgress from "@mui/material/CircularProgress";
import { X, Check, Search } from "lucide-react";

import useStore from "@/hooks/useStore";
import { getPayload as getImpersonationPayload, isActive as isImpersonationActive } from "@/impersonation/impersonationSession";
import { lookupUserByEmailForActing } from "@/store/actingUserApi";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

/**
 * Superadmin modal: same layout as {@link AccountSwitcherModal} — current signed-in admin, then local search + user list.
 * Directory rows come from getAllUsers (see userApi / fetchActingPickerUserRows); ad-hoc email lookup when not in that list.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {boolean} props.isMobile
 * @param {boolean} props.isCollapsed
 * @param {string} props.customerId
 * @param {{ name: string, email: string, initial: string }} props.signedInUserCard — Cognito superadmin (not the acting target)
 * @param {() => void} props.onStopActing
 * @param {(selection: { userId: string, email: string, displayName: string, roles?: string[] }) => void} props.onSelect
 */
const ActingUserPickerModal = observer(function ActingUserPickerModal({
  isOpen,
  onClose,
  isMobile,
  isCollapsed,
  customerId,
  signedInUserCard,
  onStopActing,
  onSelect,
}) {
  const { actingUserDirectoryStore } = useStore();
  const [searchQuery, setSearchQuery] = useState("");
  const [resolveError, setResolveError] = useState("");
  const [resolvingKey, setResolvingKey] = useState("");

  const pickerRows = actingUserDirectoryStore.getRows(customerId);
  const directoryLoading = actingUserDirectoryStore.isLoading(customerId);
  const directoryError = actingUserDirectoryStore.getError(customerId);

  const handleClose = useCallback(() => {
    setSearchQuery("");
    setResolveError("");
    setResolvingKey("");
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!isOpen || !customerId) return undefined;
    void actingUserDirectoryStore.ensureLoaded(customerId);
  }, [isOpen, customerId, actingUserDirectoryStore]);

  const signedInEmailLower = String(signedInUserCard?.email ?? "")
    .trim()
    .toLowerCase();
  const actingEmailLower = String(getImpersonationPayload()?.targetEmail ?? "")
    .trim()
    .toLowerCase();
  const isActingAsUser = isImpersonationActive();

  const filteredRows = useMemo(() => {
    const base = pickerRows.filter((row) => {
      const em = String(row.email ?? "")
        .trim()
        .toLowerCase();
      if (actingEmailLower && em === actingEmailLower) return false;
      if (signedInEmailLower && em === signedInEmailLower) return false;
      return true;
    });

    if (!searchQuery.trim()) return base;
    const q = searchQuery.toLowerCase().trim();
    return base.filter((row) => {
      const nameMatch = row.displayName?.toLowerCase().includes(q);
      const emailMatch = row.email?.toLowerCase().includes(q);
      return nameMatch || emailMatch;
    });
  }, [pickerRows, searchQuery, actingEmailLower, signedInEmailLower]);

  const adHocEmailLookup = useMemo(() => {
    const q = searchQuery.trim();
    if (!EMAIL_RE.test(q)) return "";
    const ql = q.toLowerCase();
    const inDirectory = pickerRows.some((r) => String(r.email).toLowerCase() === ql);
    if (inDirectory) return "";
    if (actingEmailLower && ql === actingEmailLower) return "";
    if (signedInEmailLower && ql === signedInEmailLower) return "";
    return ql;
  }, [searchQuery, pickerRows, actingEmailLower, signedInEmailLower]);

  const resolveAndSelect = useCallback(
    async (email) => {
      const em = String(email ?? "").trim().toLowerCase();
      if (!em || !customerId) return;
      setResolvingKey(em);
      setResolveError("");
      try {
        const row = await lookupUserByEmailForActing({
          email: em,
          expectedCustomerId: customerId,
        });
        if (!row) {
          setResolveError(`No app user found for ${em} in this account.`);
          return;
        }
        onSelect({
          userId: row.userId,
          email: row.email,
          displayName: row.displayName,
          roles: Array.isArray(row.roles) ? row.roles : [],
        });
        handleClose();
      } finally {
        setResolvingKey("");
      }
    },
    [customerId, onSelect, handleClose],
  );

  if (!isOpen || typeof document === "undefined") {
    return null;
  }

  const displayCard = signedInUserCard ?? {
    name: "User",
    email: "user@horaizen.com",
    initial: "U",
  };

  return ReactDOM.createPortal(
    <>
      <div
        className={`account-modal-backdrop ${isMobile ? "mobile-backdrop" : ""}`}
        onClick={handleClose}
        aria-hidden="true"
      />
      <div
        className={`account-modal ${isMobile ? "mobile" : "desktop"}`}
        style={
          !isMobile
            ? {
                left: isCollapsed ? "80px" : "236px",
                transition: "left 0.3s ease",
              }
            : {}
        }
        role="dialog"
        aria-modal="true"
        aria-labelledby="acting-user-current-title"
      >
        <div className="modal-section">
          <div className="modal-section-header">
            <div id="acting-user-current-title" className="modal-section-title">
              Current user
            </div>
            {isMobile && (
              <button
                type="button"
                className="account-modal-close"
                onClick={handleClose}
                aria-label="Close user switcher"
              >
                <X className="account-modal-close-icon" />
              </button>
            )}
          </div>
          <div className="current-account-card">
            <div
              className="account-avatar-small"
              style={{
                background: "#9D6AC4",
              }}
            >
              <span
                className="account-initial-small"
                style={{
                  color: "#ffffff",
                  WebkitTextFillColor: "#ffffff",
                  textFillColor: "#ffffff",
                  display: "block",
                }}
              >
                {displayCard.initial || displayCard.name?.charAt(0)?.toUpperCase() || "U"}
              </span>
            </div>
            <div className="account-info-small">
              <div className="account-name-small">{displayCard.name || "User"}</div>
              <div className="account-email-small">{displayCard.email || ""}</div>
            </div>
            <Check className="check-icon" />
          </div>
          <div className="acting-user-picker-refresh-row">
            <button
              type="button"
              className="acting-user-refresh-directory-btn"
              onClick={() => void actingUserDirectoryStore.refresh(customerId)}
              disabled={directoryLoading || !String(customerId ?? "").trim()}
            >
              Refresh user list
            </button>
          </div>
        </div>

        <div className="modal-section modal-section-scrollable">
          <div className="modal-section-title">Switch user</div>

          {isActingAsUser ? (
            <button
              type="button"
              className="acting-user-stop-acting-btn"
              onClick={() => {
                onStopActing?.();
                handleClose();
              }}
            >
              Stop acting as another user
            </button>
          ) : null}

          <div className="account-search-container">
            <Search className="account-search-icon" />
            <input
              type="text"
              autoComplete="off"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setResolveError("");
              }}
              className="account-search-input"
              aria-label="Search users in this account"
            />
          </div>

          {directoryError ? <div className="acting-user-picker-error">{directoryError}</div> : null}
          {resolveError ? <div className="acting-user-picker-error">{resolveError}</div> : null}

          <div className="account-list">
            {directoryLoading ? (
              <div
                className="acting-user-picker-loading"
                role="status"
                aria-live="polite"
                aria-busy="true"
                aria-label="Loading users"
              >
                <CircularProgress size={32} color="inherit" aria-hidden />
              </div>
            ) : (
              <>
                {adHocEmailLookup ? (
                  <button
                    type="button"
                    className="account-item"
                    onClick={() => void resolveAndSelect(adHocEmailLookup)}
                    disabled={Boolean(resolvingKey)}
                  >
                    <div
                      className="account-avatar-small"
                      style={{
                        background: "#9D6AC4",
                      }}
                    >
                      <span
                        className="account-initial-small"
                        style={{
                          color: "#ffffff",
                          WebkitTextFillColor: "#ffffff",
                          textFillColor: "#ffffff",
                          display: "block",
                        }}
                      >
                        @
                      </span>
                    </div>
                    <div className="account-info-small">
                      <div className="account-name-small" title={adHocEmailLookup}>
                        Look up {adHocEmailLookup}
                      </div>
                      <div className="account-email-small" title={adHocEmailLookup}>
                        {resolvingKey === adHocEmailLookup ? "Resolving…" : "Not in list — verify with server"}
                      </div>
                    </div>
                  </button>
                ) : null}

                {filteredRows.map((row) => (
                  <button
                    key={row.id}
                    type="button"
                    className="account-item"
                    onClick={() => void resolveAndSelect(row.email)}
                    disabled={Boolean(resolvingKey)}
                  >
                    <div
                      className="account-avatar-small"
                      style={{
                        background: "#9D6AC4",
                      }}
                    >
                      <span
                        className="account-initial-small"
                        style={{
                          color: "#ffffff",
                          WebkitTextFillColor: "#ffffff",
                          textFillColor: "#ffffff",
                          display: "block",
                        }}
                      >
                        {row.initial}
                      </span>
                    </div>
                    <div className="account-info-small">
                      <div className="account-name-small" title={row.displayName}>
                        {row.displayName}
                      </div>
                      <div className="account-email-small" title={row.email}>
                        {resolvingKey === String(row.email).toLowerCase() ? "Resolving…" : row.email}
                      </div>
                    </div>
                  </button>
                ))}

                {!directoryError && !adHocEmailLookup && filteredRows.length === 0 ? (
                  <div className="account-list-empty">No users found</div>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
});

export default ActingUserPickerModal;
